import lunr from 'lunr'

// Client-side interactions for the public registrations pages (AL-573 §6.B6). Both operate on the
// already-rendered SSR DOM — no data fetching, no client-side grouping.
const ChampionshipRegistrations = {
  initialize() {
    this._initCategoryFilters()
    this._initAcademySearch()
  },

  // By-category page: two dropdowns (belt color + gender) filter the rendered sets. Each set carries
  // its belt-group keys and its scalar gender, so we match on identity rather than string-splitting
  // the friendly name. Belts can combine ("blue,purple") and the combo is its own option; gender
  // never combines, so it is a single value. The two filters combine with AND. We toggle each
  // `.category-set` and then hide a `.belt-group` heading once none of its sets remain visible, so no
  // empty section is left behind.
  _initCategoryFilters() {
    const $belt = $('#belt-group-filter')
    const $gender = $('#gender-group-filter')
    if ($belt.length === 0 && $gender.length === 0) return

    const apply = () => {
      const belt = $belt.val() || ''
      const gender = $gender.val() || ''

      $('.belt-group').each((_, groupEl) => {
        const $group = $(groupEl)
        let anyVisible = false

        $group.find('.category-set').each((_, setEl) => {
          const $set = $(setEl)
          const setBelt = String($set.data('belt-group') || '')
          const setGender = String($set.data('gender') || '')
          const visible = (belt === '' || setBelt === belt) && (gender === '' || setGender === gender)
          $set.toggle(visible)
          if (visible) anyVisible = true
        })

        $group.toggle(anyVisible)
      })
    }

    $belt.on('change', apply)
    $gender.on('change', apply)
  },

  // By-academy page: fuzzy search over the rendered academy sections with lunr.js.
  _initAcademySearch() {
    const $input = $('#academy-search')
    const $sections = $('#registrations-by-academy .academy-set')
    if ($input.length === 0 || $sections.length === 0) return

    const $empty = $('#academy-search-empty')
    const $clear = $('#academy-search-clear')
    const $icon = $clear.find('i')

    const documents = []
    $sections.each((index, el) => {
      documents.push({ id: String(index), name: String($(el).data('academy-name') || '') })
    })

    const index = lunr(function () {
      // Academy names are proper nouns, not natural-language prose, and we match them as substrings
      // via wildcards below. lunr's default English pipeline mangles them: the Porter stemmer turns
      // "Los" into "lo" and "Angeles" into "angel" at index time, so a wildcard search for "*los*"
      // finds nothing; the stop-word filter would likewise drop names like "The Academy". Strip both
      // from the indexing and search pipelines so tokens are stored and queried verbatim.
      this.pipeline.remove(lunr.stemmer)
      this.pipeline.remove(lunr.stopWordFilter)
      this.searchPipeline.remove(lunr.stemmer)
      this.searchPipeline.remove(lunr.stopWordFilter)

      this.ref('id')
      this.field('name')
      documents.forEach((doc) => this.add(doc), this)
    })

    const runSearch = () => {
      const words = $input.val().trim().split(/\s+/).filter(Boolean)

      // The field icon doubles as a clear button: a magnifier while empty, an X once there's text.
      const hasText = words.length > 0
      $icon.toggleClass('fa-search', !hasText).toggleClass('fa-times', hasText)
      $clear.attr('aria-label', $clear.data(hasText ? 'clearLabel' : 'searchLabel'))

      if (!hasText) {
        $sections.show()
        $empty.prop('hidden', true)
        return
      }

      // Require *every* typed word to be present (AND), each matched as a substring via
      // leading+trailing wildcards. lunr's default search() is OR, so "Team Cruz BJJ" would
      // otherwise surface any academy sharing a common token like "Team" or "BJJ".
      const results = index.query((query) => {
        words.forEach((word) => {
          query.term(word.toLowerCase(), {
            wildcard: lunr.Query.wildcard.LEADING | lunr.Query.wildcard.TRAILING,
            presence: lunr.Query.presence.REQUIRED,
          })
        })
      })

      const matches = new Set(results.map((result) => result.ref))
      $sections.each((position, el) => $(el).toggle(matches.has(String(position))))

      // Announce "no academies found" via the aria-live region instead of leaving a blank page.
      $empty.prop('hidden', matches.size > 0)
    }

    $input.on('input', runSearch)

    // Clicking the X clears the field; clicking the magnifier just focuses it.
    $clear.on('click', () => {
      if ($input.val().trim() !== '') {
        $input.val('')
        runSearch()
      }
      $input.trigger('focus')
    })
  },
}

export default ChampionshipRegistrations
